import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';
import { PerformanceMonitorService } from './performance-monitor.service';

/** Minimum time between repeat toasts for the same error message, in ms. */
const REPEAT_TOAST_SUPPRESS_MS = 4000;

/**
 * Matches pdf.js's internal teardown races — thrown when a document/page teardown
 * (e.g. navigating away from the PDF viewer step, or switching to a different PDF) races an
 * in-flight async operation against the same document. Both are benign timing issues in the
 * vendor library — the app keeps working — so they're logged but shouldn't alarm the user with
 * an "unexpected error" toast:
 *  - `WorkerTransport.startCleanup` guard: teardown races an in-flight page render.
 *  - `Transport destroyed`: a pending callback still fires after the document's transport has
 *    already been destroyed (e.g. `PdfPageViewerComponent` swapping sources while a previous
 *    page render was still in flight) — reproducible by leaving step 3 (Assign Floors) for step
 *    2 right after the PDF finishes rendering.
 */
const PDF_CLEANUP_RACE_PATTERN = /startCleanup: Page \d+ is currently rendering|Transport destroyed/;

/**
 * Catches uncaught application errors — component/template exceptions and
 * unhandled promise rejections routed here via `provideBrowserGlobalErrorListeners()`
 * — that would otherwise only be visible in the browser console.
 *
 * This is the single place uncaught errors flow through; swap PerformanceMonitorService
 * for a real telemetry vendor (Sentry, LogRocket, etc.) in `report()` if one is adopted.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly monitor = inject(PerformanceMonitorService);

  private lastNotifiedAt = 0;

  handleError(error: unknown): void {
    // HTTP errors already get a user-facing toast from errorInterceptor and
    // are re-thrown through the Observable chain, not the ErrorHandler — but
    // guard here too in case one ever surfaces via an unhandled rejection.
    if (error instanceof HttpErrorResponse) {
      this.report(error);
      return;
    }

    this.report(error);
    if (!this.isPdfCleanupRace(error)) this.notifyUser();
  }

  private isPdfCleanupRace(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return PDF_CLEANUP_RACE_PATTERN.test(message);
  }

  private report(error: unknown): void {
    console.error('[GlobalErrorHandler]', error);
    this.monitor.recordError(error, error instanceof HttpErrorResponse ? 'http' : 'runtime');
  }

  private notifyUser(): void {
    const now = Date.now();
    if (now - this.lastNotifiedAt < REPEAT_TOAST_SUPPRESS_MS) return;
    this.lastNotifiedAt = now;

    try {
      this.toast.error(
        this.translate.instant('COMMON.UNEXPECTED_ERROR_TITLE'),
        this.translate.instant('COMMON.UNEXPECTED_ERROR_MESSAGE'),
      );
    } catch {
      // Notifying the user must never itself throw from inside an error handler.
    }
  }
}
